// DOM helpers, widgets and formatters every page uses. Nothing here talks
// to the API.

import type { Capacity, Geo } from "./types";

export const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

export const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string | null) => {
    try {
      if (v == null) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    } catch {
      // private mode
    }
  },
};

// ---------- the period (7, 30 or 90 days) the usage pages show ----------

let daysValue = Number(store.get("luwu-admin-days")) || 30;
export const days = () => daysValue;
export const setDays = (n: number) => {
  daysValue = n;
  store.set("luwu-admin-days", String(n));
};

// ---------- formatting ----------

export const size = (n: number) =>
  n < 1024 ? `${n} B` : n < 1 << 20 ? `${(n / 1024).toFixed(1)} KB` : n < 1 << 30 ? `${(n / (1 << 20)).toFixed(1)} MB` : `${(n / (1 << 30)).toFixed(2)} GB`;
/** Decimal gigabytes, as providers count transfer. */
export const gb = (b: number) => (b >= 1e12 ? `${(b / 1e12).toFixed(2)} TB` : `${(b / 1e9).toFixed(b < 1e10 ? 2 : 0)} GB`);
export const rate = (bps: number) => (bps < 1024 ? `${bps} B/s` : bps < 1 << 20 ? `${(bps / 1024).toFixed(1)} KB/s` : `${(bps / (1 << 20)).toFixed(2)} MB/s`);
export const hours = (s: number) => (s < 3600 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);
export const uptime = (s: number | null) => (s == null ? "—" : s < 3600 ? `${Math.round(s / 60)} min` : s < 86400 ? `${(s / 3600).toFixed(1)} h` : `${Math.floor(s / 86400)} d ${Math.round((s % 86400) / 3600)} h`);
export const money = (n: number) => `$${n.toFixed(2)}`;
export const num = (n: number) => n.toLocaleString();
export const date = (unix: number) => new Date(unix * 1000).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
export const dateTime = (unix: number) => new Date(unix * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
export const ago = (unix: number | null | undefined) => {
  if (!unix) return "—";
  const s = Date.now() / 1000 - unix;
  if (s < 120) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return date(unix);
};
export const who = (a: { username?: string | null; email?: string | null; id?: string; account_id?: string }) =>
  a.username || a.email?.split("@")[0] || (a.id ?? a.account_id ?? "").slice(0, 8);

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();
export const flag = (cc: string) => (/^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((ch) => 0x1f1a5 + ch.charCodeAt(0))) : "");
export const countryName = (cc: string) => (cc === "T1" ? "Tor" : cc === "XX" ? "unknown" : (regionNames?.of(cc) ?? cc));
/** "🇺🇸 Los Angeles, United States" ("T1" is Tor, "XX" unknown, per Cloudflare). */
export function place(g: Geo & { ip?: string | null }) {
  const cc = g.country ?? "";
  if (!cc && !g.city) return "—";
  return [flag(cc), [g.city, cc ? countryName(cc) : ""].filter(Boolean).join(", ")].filter(Boolean).join(" ");
}
/** A table cell: place on top, IP below. */
export const geoCell = (g: Geo & { ip?: string | null }) => {
  const ip = g.last_ip ?? g.ip;
  return h("td", null, place(g), ip ? h("span", { class: "muted block mono" }, ip) : null);
};

/** Every day from `from` to `to` (UTC dates as YYYY-MM-DD). */
export function dayRange(from: string, to: string) {
  const out: string[] = [];
  for (let t = Date.parse(`${from}T00:00:00Z`); t <= Date.parse(`${to}T00:00:00Z`); t += 86400_000) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}

// ---------- DOM ----------

export type Child = Node | string | null | undefined | false;
export function h(tag: string, attrs?: Record<string, string> | null, ...kids: Child[]): HTMLElement {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs ?? {})) {
    if (k === "class") e.className = v;
    else e.setAttribute(k, v);
  }
  for (const k of kids) if (k !== null && k !== undefined && k !== false) e.append(typeof k === "string" ? document.createTextNode(k) : k);
  return e;
}
export const td = (text: string, cls = "") => h("td", cls ? { class: cls } : null, text);
export const th = (text: string, cls = "") => h("th", cls ? { class: cls } : null, text);
export const link = (href: string, text: string) => h("a", { href }, text);
export const userLink = (id: string, text: string) => link(`/user?id=${encodeURIComponent(id)}`, text);
export const nodeLink = (name: string, text = name) => link(`/node?relay=${encodeURIComponent(name)}`, text);

/** A form field: label above the control. */
export function field(label: string, control: HTMLElement, wide = false) {
  return h("label", wide ? { class: "wide" } : null, label, control);
}
export const input = (attrs: Record<string, string>) => h("input", attrs) as HTMLInputElement;
export const select = (attrs: Record<string, string>, options: [string, string][], value?: string) => {
  const s = h("select", attrs, ...options.map(([v, label]) => h("option", { value: v }, label))) as HTMLSelectElement;
  if (value !== undefined) s.value = value;
  return s;
};
export const textarea = (attrs: Record<string, string>, value = "") => {
  const t = h("textarea", attrs) as HTMLTextAreaElement;
  t.value = value;
  return t;
};
export const button = (label: string, cls: string, onClick: () => void) => {
  const b = h("button", { type: "button", class: cls }, label) as HTMLButtonElement;
  b.addEventListener("click", onClick);
  return b;
};

export function table(head: HTMLElement[], rows: HTMLElement[], empty: string) {
  const body = h("tbody", null, ...rows);
  if (!rows.length) body.append(h("tr", null, h("td", { colspan: String(head.length), class: "muted" }, empty)));
  return h("div", { class: "table-wrap" }, h("table", null, h("thead", null, h("tr", null, ...head)), body));
}

export function figures(items: [string, string, string?][]) {
  return h("div", { class: "figures" }, ...items.map(([label, value, sub]) => h("div", { class: "fig" }, h("span", { class: "label" }, label), h("span", { class: "value" }, value), sub ? h("span", { class: "sub" }, sub) : null)));
}

export const statusChip = (s: string, title?: string) => h("span", { class: `chip ${s}`, ...(title ? { title } : {}) }, s);

const PALETTE = ["var(--c1)", "var(--c2)", "var(--c3)", "var(--c4)"];
const FIXED: Record<string, string> = { out: "var(--c1)", in: "var(--c4)" };
const assigned = new Map<string, string>();
/** A stable colour per series name (relay names get one in order of first use). */
export function color(name: string) {
  const fixed = FIXED[name];
  if (fixed) return fixed;
  let c = assigned.get(name);
  if (!c) {
    c = PALETTE[assigned.size % PALETTE.length]!;
    assigned.set(name, c);
  }
  return c;
}

/** Daily stacked bars. series: name → value per day. */
export function chart(dayList: string[], series: { name: string; label: string; values: Map<string, number> }[], fmt: (n: number) => string) {
  const max = Math.max(1, ...dayList.map((d) => series.reduce((t, s) => t + (s.values.get(d) ?? 0), 0)));
  const bars = h("div", { class: "chart", role: "img", "aria-label": series.map((s) => s.label).join(", ") + " per day" });
  for (const d of dayList) {
    const col = h("div", { class: "col" });
    col.title = `${d}: ` + series.map((s) => `${s.label} ${fmt(s.values.get(d) ?? 0)}`).join(", ");
    for (const s of series) {
      const seg = h("i");
      seg.style.height = `${(100 * (s.values.get(d) ?? 0)) / max}%`;
      seg.style.background = color(s.name);
      col.append(seg);
    }
    bars.append(col);
  }
  const legend = h("div", { class: "legend" }, ...series.map((s) => h("span", null, h("i", { style: `background:${color(s.name)}` }), s.label)));
  return h(
    "div",
    { class: "chart-wrap" },
    h("div", { class: "axis" }, h("span", null, fmt(max)), legend),
    bars,
    h("div", { class: "axis" }, h("span", null, dayList[0] ?? ""), h("span", null, dayList.at(-1) ?? "")),
  );
}

const SVG = "http://www.w3.org/2000/svg";
/** A small line of values (oldest first), for trends in cards and tables. */
export function sparkline(values: number[], stroke: string, label: string) {
  const w = 160, hgt = 32;
  const svg = document.createElementNS(SVG, "svg");
  svg.setAttribute("viewBox", `0 0 ${w} ${hgt}`);
  svg.setAttribute("class", "spark");
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", label);
  const max = Math.max(1, ...values);
  if (values.length > 1) {
    const pts = values.map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(hgt - 2 - (v / max) * (hgt - 4)).toFixed(1)}`);
    const area = document.createElementNS(SVG, "polygon");
    area.setAttribute("points", `0,${hgt} ${pts.join(" ")} ${w},${hgt}`);
    area.setAttribute("fill", stroke);
    area.setAttribute("opacity", "0.15");
    const line = document.createElementNS(SVG, "polyline");
    line.setAttribute("points", pts.join(" "));
    line.setAttribute("fill", "none");
    line.setAttribute("stroke", stroke);
    line.setAttribute("stroke-width", "1.5");
    svg.append(area, line);
  } else {
    const t = document.createElementNS(SVG, "text");
    t.setAttribute("x", "0");
    t.setAttribute("y", "20");
    t.setAttribute("class", "spark-empty");
    t.textContent = "no samples yet";
    svg.append(t);
  }
  return svg as unknown as HTMLElement;
}

const VERDICT: Record<string, string> = { ok: "room", busy: "busy", full: "full", unknown: "—" };
export const loadChip = (c: Capacity) =>
  h("span", { class: `chip load-${c.verdict}`, title: c.advice }, c.load == null ? "load —" : `${VERDICT[c.verdict]} · ${Math.round(c.load)}%`);

/** A labelled bar per resource; the limiting one is marked. */
export function meters(c: Capacity, compact = false) {
  if (!c.meters.length) return h("p", { class: "muted small" }, c.advice);
  return h(
    "div",
    { class: compact ? "meters compact" : "meters" },
    ...c.meters
      .filter((m) => !compact || m.pct != null)
      .map((m) => meter(m.label, m.pct, m.detail, m.key === c.limit, compact)),
  );
}

/** One bar: label, fill, percentage and (unless compact) the detail line. */
export function meter(label: string, pct: number | null, detail: string, limit = false, compact = false) {
  const level = pct == null ? "none" : pct >= 80 ? "full" : pct >= 60 ? "busy" : "ok";
  const bar = h("span", { class: "mbar" }, h("i", { class: level, style: `width:${Math.min(100, pct ?? 0)}%` }));
  return h(
    "div",
    { class: `meter${limit ? " limit" : ""}`, title: detail },
    h("span", { class: "m-label" }, label),
    bar,
    h("span", { class: "m-pct" }, pct == null ? "—" : `${Math.round(pct)}%`),
    compact ? null : h("span", { class: "m-detail muted" }, detail),
  );
}

/** "1–50 of 1,234" with previous and next. */
export function pager(total: number, limit: number, offset: number, go: (offset: number) => void) {
  const prev = h("button", { type: "button", class: "btn plain small" }, "‹ Previous") as HTMLButtonElement;
  const next = h("button", { type: "button", class: "btn plain small" }, "Next ›") as HTMLButtonElement;
  prev.disabled = offset <= 0;
  next.disabled = offset + limit >= total;
  prev.addEventListener("click", () => go(Math.max(0, offset - limit)));
  next.addEventListener("click", () => go(offset + limit));
  const pages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(offset / limit) + 1;
  return h(
    "div",
    { class: "pager" },
    h("span", { class: "muted" }, total ? `${num(offset + 1)}–${num(Math.min(offset + limit, total))} of ${num(total)} · page ${page} of ${pages}` : "Nothing to show"),
    h("span", { class: "tools" }, prev, next),
  );
}

export function debounce<T extends unknown[]>(f: (...a: T) => void, ms = 300) {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...a: T) => {
    clearTimeout(t);
    t = setTimeout(() => f(...a), ms);
  };
}

/** A button that asks once ("Block Martin?") before doing it. */
export function confirmButton(label: string, armed: string, run: () => Promise<void>, cls = "btn small danger") {
  const b = h("button", { class: cls, type: "button" }, label) as HTMLButtonElement;
  b.addEventListener("click", async () => {
    if (!b.classList.contains("armed")) {
      b.classList.add("armed");
      b.textContent = armed;
      setTimeout(() => {
        if (!b.disabled) {
          b.classList.remove("armed");
          b.textContent = label;
        }
      }, 6000);
      return;
    }
    b.disabled = true;
    try {
      await run();
    } catch (e) {
      b.disabled = false;
      b.classList.remove("armed");
      b.textContent = label;
      flash((e as Error).message, true);
    }
  });
  return b;
}

/** A button that runs an action, disabled while it runs; errors go to the flash line. */
export function actionButton(label: string, run: () => Promise<void>, cls = "btn small plain") {
  const b = h("button", { class: cls, type: "button" }, label) as HTMLButtonElement;
  b.addEventListener("click", async () => {
    b.disabled = true;
    try {
      await run();
    } catch (e) {
      flash((e as Error).message, true);
    } finally {
      b.disabled = false;
    }
  });
  return b;
}

/** A form whose submit runs `run`; errors go to the flash line. */
export function form(cls: string, run: () => Promise<void>, ...kids: Child[]) {
  const f = h("form", { class: cls }, ...kids) as HTMLFormElement;
  f.addEventListener("submit", async (e) => {
    e.preventDefault();
    const buttons = [...f.querySelectorAll<HTMLButtonElement>("button[type=submit]")];
    for (const b of buttons) b.disabled = true;
    try {
      await run();
    } catch (x) {
      flash((x as Error).message, true);
    } finally {
      for (const b of buttons) b.disabled = false;
    }
  });
  return f;
}

/** One line of feedback at the top of the page. */
export function flash(text: string, error = false) {
  let el = document.getElementById("flash");
  if (!el) {
    el = h("p", { id: "flash" });
    $("page").prepend(el);
  }
  el.className = error ? "error" : "okmsg";
  el.textContent = text;
  el.scrollIntoView({ block: "nearest" });
}

/**
 * Something shown once (an enrol command, a key): a copyable block with a
 * warning that it won't be shown again.
 */
export function secret(title: string, lines: string[], note: string) {
  const pre = h("pre", null, lines.join("\n"));
  const copy = button("Copy", "btn small plain", async () => {
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      copy.textContent = "Copied";
      setTimeout(() => (copy.textContent = "Copy"), 2000);
    } catch {
      copy.textContent = "Select the text and copy it";
    }
  });
  return h("div", { class: "secret", role: "alert" }, h("b", null, title), pre, h("div", { class: "actions" }, copy, h("span", { class: "note-line" }, note)));
}

/** The expandable JSON of an event's detail. */
export function detailBox(detail: Record<string, unknown> | undefined) {
  if (!detail || !Object.keys(detail).length) return h("span", { class: "muted" }, "—");
  return h("details", { class: "detail" }, h("summary", null, "detail"), h("pre", null, JSON.stringify(detail, null, 2)));
}

// ---------- the page frame ----------

const views = ["email", "code", "loading", "denied", "app"] as const;
export type View = (typeof views)[number];
export function view(v: View) {
  for (const id of views) $(`v-${id}`).hidden = id !== v;
  // The menu, the period buttons and Sign out belong to a signed-in staff
  // member; the sign-in and "not staff" views show the mark alone.
  for (const sel of [".bar nav.tabs", ".bar .tools"]) {
    const el = document.querySelector<HTMLElement>(sel);
    if (el) el.hidden = v !== "app";
  }
}

export const main = () => $("page");
export function show(title: string, sub: string, ...content: Child[]) {
  document.title = `${title} · Luwu admin`;
  main().replaceChildren(h("header", { class: "page-head" }, h("div", null, h("h1", null, title), h("p", null, sub))), ...content.filter(Boolean).map((c) => (typeof c === "string" ? h("p", null, c) : (c as Node))));
  view("app");
}

/** "2025-10-26T14:05" in local time, for datetime-local inputs. */
export const localDateTime = (unix: number) => {
  const d = new Date(unix * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
/** The reverse: a datetime-local value as unix seconds, 0 when empty. */
export const unixOf = (local: string) => (local ? Math.floor(Date.parse(local) / 1000) || 0 : 0);
